import { combineLatest, fromEvent, map, skip, startWith } from "rxjs";

export default class ZipInput {
  constructor() {
    this.valueInput = '';
    this.isInternationalInput  = false;
    this.errorMessageId = 'nhs_ErrorForipCodeReg';
    this.idForOutside = 'livesOutside';
    this.idForZip = 'postalCode';
    this.errorCssClass = 'error';
    this.labelClassName = 'nhs-u-text--visually-hidden';
    this.fieldClassName = 'form__input form__zip';
    this.errorClassName = 'form__error zip__error';
    this.checkboxWrapperClassName = 'field--checkbox';
    this.checkboxClassName = 'form__checkbox';
    this.checkboxLabelClassName = 'form__text';
    this.labelText = 'Zip Code';
    this.required = true;
    this.pattern = '^\\d{5}(-\\d{4})?$';
    this.matchingPattern = new RegExp(this.pattern);
  }

  set isInternational(newValue){
    this.isInternationalInput = !!newValue;
  }

  get isInternational(){
    return this.isInternationalInput;
  }

  set value(newValue){
    this.valueInput = newValue || '';
  }

  get value(){
    return this.valueInput;
  }

  /**
   * Overrides the CSS classes used by getHtml(). Defaults (set in the constructor) match every
   * existing consumer of this component; only screens that want different styling need to call
   * this — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {Object} classNames
   * @param {string} [classNames.labelClassName]
   * @param {string} [classNames.fieldClassName]
   * @param {string} [classNames.errorClassName]
   * @param {string} [classNames.checkboxWrapperClassName]
   * @param {string} [classNames.checkboxClassName]
   * @param {string} [classNames.checkboxLabelClassName]
   */
  setClassNames({
    labelClassName, fieldClassName, errorClassName, checkboxWrapperClassName, checkboxClassName, checkboxLabelClassName,
  } = {}) {
    if (labelClassName !== undefined) {
      this.labelClassName = labelClassName;
    }
    if (fieldClassName !== undefined) {
      this.fieldClassName = fieldClassName;
    }
    if (errorClassName !== undefined) {
      this.errorClassName = errorClassName;
    }
    if (checkboxWrapperClassName !== undefined) {
      this.checkboxWrapperClassName = checkboxWrapperClassName;
    }
    if (checkboxClassName !== undefined) {
      this.checkboxClassName = checkboxClassName;
    }
    if (checkboxLabelClassName !== undefined) {
      this.checkboxLabelClassName = checkboxLabelClassName;
    }
    return this;
  }

  /**
   * Overrides the visible label text of the ZIP field (not the "Outside U.S." checkbox's own
   * label, which is unaffected). Default (set in the constructor) matches every existing consumer
   * of this component; only screens that want different copy need to call this — see
   * GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} text
   */
  setLabelText(text) {
    this.labelText = text;
    return this;
  }

  /**
   * Overrides the error-message element's id used by getHtml(). Default (set in the constructor)
   * matches every existing consumer of this component; a screen that can render alongside another
   * instance of this component on the same page (e.g. a persistent sidebar form) must override this
   * to avoid a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} errorMessageId
   */
  setErrorMessageId(errorMessageId) {
    this.errorMessageId = errorMessageId;
    return this;
  }

  bindEvents() {
    this.getOutsideUsNode().addEventListener('change', this.onOutsideUsChanged.bind(this));
  }

  /**
   * @returns the HTML for the ZIP field itself (label + input + error), without the "Outside U.S."
   * checkbox. Split out from getHtml() so callers that lay out fields as flat siblings (e.g.
   * RequestATourForm/GetMoreInformationForm, NHS20-8178) can give the checkbox its own
   * field-wrapper instead of nesting it inside the ZIP field's — nesting it there made it inherit
   * the ZIP field's label-to-input gap instead of the larger gap Figma uses between field groups.
   *
   * Returns label/input/error as flat siblings (no wrapping div) to match every other field
   * component (NameInput, EmailInput, PhoneInput) — their shared caller's own field-wrapper div
   * supplies the gap between them directly. this.fieldClassName's own consumers (nhs-c-input__field,
   * form__input) already set their own width, so no wrapping div is needed for that either.
   */
  getFieldHtml() {
    return `<label for="${this.idForZip}" class="${this.labelClassName}">${this.labelText}</label>
            <input data-qa="zip-input" data-segment-identity-data="zip" id="${this.idForZip}" class="${this.fieldClassName}"
              placeholder="${this.required ? '*' : ''}Zip Code" name="${this.idForZip}" autocomplete="postal-code"
              maxlength="10" type="tel" ${this.required ? 'required' : ''}
              ${this.isInternational ? 'disabled="true"' : `value="${this.value}"`} data-form-zip-input></input>
            <span id="${this.errorMessageId}" class="${this.errorClassName}" hidden>
                         Please enter a valid zip code
                       </span>`;
  }

  /**
   * @returns the HTML for the "Outside U.S." checkbox row on its own — see getFieldHtml().
   */
  getCheckboxHtml() {
    return `<div class="${this.checkboxWrapperClassName}">
            <input data-qa="outside-us-input" data-segment-identity-data="outside_us" type="checkbox" id="${this.idForOutside}"
                name="${this.idForOutside}" class="${this.checkboxClassName}"
                ${this.isInternational ? 'checked="checked"' : ''} data-form-lives-outside-input></input>
              <label for="${this.idForOutside}" class="${this.checkboxLabelClassName}">Outside U.S.</label>
            </div>`;
  }

  getHtml() {
    return `${this.getFieldHtml()}${this.getCheckboxHtml()}`;
  }

  getOutsideUsNode() {
    return document.getElementById(this.idForOutside);
  }

  getZipNode() {
    return document.getElementById(this.idForZip);
  }

  /**
   * getOnChangeEvent: On inputs change map their values to the storage properties.
   */
  getOnChangeEvent() {
    const outSideNode = this.getOutsideUsNode();
    const zipNode = this.getZipNode();
    const onOutSideChange$ = fromEvent(outSideNode, 'change').pipe(
      map((event) => event.target),
      startWith(outSideNode)
    );
    const onZipChange$ = fromEvent(zipNode, 'change').pipe(
      map((event) => event.target),
      startWith(zipNode)
    );
    return combineLatest([onZipChange$, onOutSideChange$]).pipe(
      map(([zipInput, livesOutsideInput]) => ({
        zip: zipInput.value,
        livesOutside: livesOutsideInput.checked,
      })),
      skip(1)
    );
  }

  isOutsideUs() {
    return this.getOutsideUsNode().checked;
  }

  isValidZipCode() {
    const node = this.getZipNode();
    const value = node.value?.trim();
    if (!value) {
      return !this.required;
    }
    return this.matchingPattern.test(value);
  }

  onOutsideUsChanged(event) {
    const node = this.getZipNode();

    if (event.currentTarget.checked) {
      node.setAttribute('disabled', true);
      node.value = '';
      this.setValidState(node);
    } else {
      node.removeAttribute('disabled', true);
    }
  }

  setInvalidState() {
    const errorMessage = document.getElementById(this.errorMessageId);
    const node = this.getZipNode();

    node.setAttribute('aria-invalid', true);
    node.classList.add(this.errorCssClass);
    errorMessage.removeAttribute('hidden');
  }

  setValidState() {
    const errorMessage = document.getElementById(this.errorMessageId);
    const node = this.getZipNode();

    node.removeAttribute('aria-invalid');
    node.classList.remove(this.errorCssClass);
    errorMessage.setAttribute('hidden', true);
  }

  validate() {
    const isOutsideUs = this.isOutsideUs();
    const isValidZipCode = this.isValidZipCode();
    const isValid = isOutsideUs || isValidZipCode;

    return new Promise((resolve) => {
      if (isValid) {
        this.setValidState();
        resolve(true);
      } else {
        this.setInvalidState();
        resolve(false);
      }
    });
  }
}
